/**
 * @engine/vfx — Tweakable inputs (editor live-preview).
 *
 * In tweak mode the compiler routes unconnected float-family inputs through a
 * uniform slot instead of baking them as literals: the generated code reads
 * `vfxVariables.tweaks[slot]` and the editor writes the buffer on slider drags
 * — no recompilation. Structural inputs (int/bool/enum — they steer loops and
 * branches) and gradient/curve data stay baked and still recompile.
 *
 * Slots are float4-packed (one input per slot, capacity {@link TWEAK_CAPACITY})
 * and allocated by a per-system allocator SHARED between the init and update
 * stage compilations, so both modules agree on the layout. The tweak array is
 * appended to the VFXVariables struct (same binding); the renderer sizes the
 * buffer accordingly when a compiled system carries tweaks.
 */

import type { VFXDataType } from '../../types';

/** Max tweak slots per system (float4 each → 1 KiB appended to the buffer). */
export const TWEAK_CAPACITY = 64;

/** Byte offset of the tweak array inside the variables buffer (after the 384-byte VFXVariables). */
export const TWEAK_BUFFER_OFFSET = 384;

/** Total variables-buffer size when a system has tweaks. */
export const TWEAK_BUFFER_SIZE = TWEAK_BUFFER_OFFSET + TWEAK_CAPACITY * 16;

/** Port types that can be routed through a tweak slot. */
const TWEAKABLE_TYPES: ReadonlySet<string> = new Set(['float', 'vec2', 'vec3', 'vec4', 'color']);

export function isTweakableType(type: VFXDataType): boolean {
  return TWEAKABLE_TYPES.has(type);
}

/** One tweak binding: which port lives in which slot. */
export interface VFXTweakBinding {
  readonly nodeId: string;
  readonly portId: string;
  readonly slot: number;
  readonly type: VFXDataType;
  /** Value baked at compile time (the buffer's initial contents). */
  readonly value: unknown;
}

/**
 * Pack a JS port value into the float4 slot contents. Mirrors the literal
 * baking in `valueToSlang` (incl. the HDR color intensity multiplier) so a
 * tweak write produces exactly what the baked literal would have.
 */
export function tweakValueToFloats(value: unknown, type: VFXDataType): [number, number, number, number] {
  const num = (v: unknown, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const obj = (value ?? {}) as Record<string, unknown>;

  switch (type) {
    case 'float':
      return [num(value), 0, 0, 0];
    case 'vec2':
      return typeof value === 'object' && value !== null
        ? [num(obj.x), num(obj.y), 0, 0]
        : [num(value), num(value), 0, 0];
    case 'vec3':
      return typeof value === 'object' && value !== null
        ? [num(obj.x), num(obj.y), num(obj.z), 0]
        : [num(value), num(value), num(value), 0];
    case 'vec4':
    case 'color': {
      if (typeof value === 'object' && value !== null) {
        const intensity = obj.intensity !== undefined ? num(obj.intensity, 1) : 1;
        const r = num(obj.x ?? obj.r) * intensity;
        const g = num(obj.y ?? obj.g) * intensity;
        const b = num(obj.z ?? obj.b) * intensity;
        const a = obj.w !== undefined || obj.a !== undefined ? num(obj.w ?? obj.a, 1) : 1;
        return [r, g, b, a];
      }
      return [num(value), num(value), num(value), 1];
    }
    default:
      return [0, 0, 0, 0];
  }
}

/** Swizzle for reading a typed value out of a float4 tweak slot. */
export function tweakAccessExpr(slot: number, type: VFXDataType): string {
  const base = `vfxVariables.tweaks[${slot}]`;
  switch (type) {
    case 'float': return `${base}.x`;
    case 'vec2': return `${base}.xy`;
    case 'vec3': return `${base}.xyz`;
    case 'vec4':
    case 'color': return base;
    default: return `${base}.x`;
  }
}

/**
 * Per-system tweak slot allocator. Create ONE per system compile and pass it
 * to both stage generators — slot assignment must match across modules.
 */
export class TweakAllocator {
  private readonly slots = new Map<string, VFXTweakBinding>();

  /**
   * Get (or allocate) the slot for a port. Returns null when the capacity is
   * exhausted — the caller falls back to baking the literal.
   */
  allocate(nodeId: string, portId: string, type: VFXDataType, value: unknown): VFXTweakBinding | null {
    const key = `${nodeId}:${portId}`;
    const existing = this.slots.get(key);
    if (existing) return existing;
    if (this.slots.size >= TWEAK_CAPACITY) return null;
    const binding: VFXTweakBinding = { nodeId, portId, slot: this.slots.size, type, value };
    this.slots.set(key, binding);
    return binding;
  }

  /** All allocated bindings, in slot order. */
  all(): readonly VFXTweakBinding[] {
    return [...this.slots.values()];
  }
}
